import type { XYPosition } from '@xyflow/react'
import { v7 as uuidv7 } from 'uuid'
import { create } from 'zustand'
import { type CustomEdge, type CustomNode, type ExtendedConnection, StepType } from '../types'
import type { CycleStepDiagramDefinition } from '../utils'

interface CycleStepsState {
	nodes: CustomNode[]
	edges: CustomEdge[]
	hasChanges: boolean
	selectedNode: CustomNode | null
	editingNode: CustomNode | null
	diagramDefinition: CycleStepDiagramDefinition | null

	setNodes: (nodes: CustomNode[], markAsChanged?: boolean) => void
	addNode: (label: string, stepType: StepType, position?: XYPosition) => void
	updateNode: (id: string, updates: Partial<CustomNode>) => void
	deleteNode: (id: string) => void

	setEdges: (edges: CustomEdge[], markAsChanged?: boolean) => void
	connectNodes: (connection: ExtendedConnection) => void
	updateEdge: (id: string, updates: Partial<CustomEdge>) => void
	deleteEdge: (id: string) => void

	setHasChanges: (value: boolean) => void
	selectNode: (nodeId?: string) => void
	editNode: (nodeId?: string) => void
	resetChanges: () => void
	setDiagramDefinition: (definition: CycleStepDiagramDefinition) => void
}

export const useCycleStepsStore = create<CycleStepsState>((set, get) => ({
	nodes: [],
	edges: [],
	hasChanges: false,
	selectedNode: null,
	editingNode: null,
	diagramDefinition: null,

	setNodes: (nodes, markAsChanged = true) =>
		set((state) => ({ nodes, hasChanges: markAsChanged ? true : state.hasChanges })),

	addNode: (label, stepType, position = { x: 250, y: 200 }) => {
		const newNodeId = uuidv7()
		const nodes = get().nodes

		const nonEndNodes = nodes.filter((node) => node.data.stepType !== StepType.CYCLE_END)
		const baseY = 200
		const yPosition = nonEndNodes.length > 1 ? Math.max(...nonEndNodes.map((node) => node.position.y)) + 150 : baseY

		const newNode: CustomNode = {
			id: newNodeId,
			data: {
				label,
				stepType,
				onEdit: (id: string) => get().editNode(id),
				onSelect: (id: string) => get().selectNode(id),
				onDelete: (id: string) => get().deleteNode(id),
			},
			position: { x: position.x, y: yPosition },
			type: 'customNode',
		}

		let updatedNodes = [...nodes, newNode]
		const endNode = nodes.find((node) => node.data.stepType === StepType.CYCLE_END)
		if (endNode) {
			updatedNodes = updatedNodes.map((node) =>
				node.data.stepType === StepType.CYCLE_END
					? { ...node, position: { ...node.position, y: yPosition + 150 } }
					: node,
			)
		}

		set({ nodes: updatedNodes, hasChanges: true })
	},

	updateNode: (id, updates) => {
		set((state) => ({
			nodes: state.nodes.map((node) => (node.id === id ? { ...node, ...updates } : node)),
			hasChanges: true,
		}))
	},

	deleteNode: (id) => {
		set((state) => ({
			nodes: state.nodes.filter((node) => node.id !== id),
			edges: state.edges.filter((edge) => edge.source !== id && edge.target !== id),
			hasChanges: true,
		}))
	},

	setEdges: (edges, markAsChanged = true) =>
		set((state) => ({ edges, hasChanges: markAsChanged ? true : state.hasChanges })),

	connectNodes: (connection) => {
		const { edges, nodes, diagramDefinition } = get()

		if (!connection.source || !connection.target || !diagramDefinition) return
		const sourceNode = nodes.find((node) => node.id === connection.source)
		const targetNode = nodes.find((node) => node.id === connection.target)

		if (!sourceNode?.data.stepType || !targetNode?.data.stepType) return

		const sourceHandle = connection.sourceHandle || 'source-approve'
		const targetHandle = connection.targetHandle || 'target-approve'

		const isRejectConnection = sourceHandle.includes('reject') || targetHandle.includes('reject')

		const sourceStepDefinition = diagramDefinition[sourceNode.data.stepType]
		if (!sourceStepDefinition) return

		let selectedAction = null

		if (isRejectConnection) {
			selectedAction = sourceStepDefinition.possibleActions.find((action) => action.key === 'reject')
		} else {
			selectedAction = sourceStepDefinition.possibleActions.find(
				(action) => action.key !== 'reject' && action.movesForward,
			)
		}

		if (!selectedAction) {
			return
		}

		if (
			selectedAction.allowedNextSteps !== null &&
			!selectedAction.allowedNextSteps.includes(targetNode.data.stepType)
		) {
			return
		}

		const edgeLabel = connection.data?.label || (selectedAction.key === 'reject' ? 'Reject' : 'Approve')

		const newEdge: CustomEdge = {
			id: uuidv7(),
			source: connection.source,
			target: connection.target,
			sourceHandle: isRejectConnection ? 'source-reject' : 'source-approve',
			targetHandle: isRejectConnection ? 'target-reject' : 'target-approve',
			type: 'customEdge',
			data: {
				label: edgeLabel,
				key: selectedAction.key,
				movesForward:
					connection.data?.movesForward !== undefined ? connection.data.movesForward : selectedAction.movesForward,
				allowedNextSteps: selectedAction.allowedNextSteps,
				sourceHandle: isRejectConnection ? 'source-reject' : 'source-approve',
				targetHandle: isRejectConnection ? 'target-reject' : 'target-approve',
				onDelete: (id: string) => {
					get().deleteEdge(id)
					get().setHasChanges(true)
				},
			},
		}

		const existingEdgeIndex = edges.findIndex(
			(e) => e.source === connection.source && e.target === connection.target && e.data?.key === selectedAction.key,
		)

		if (existingEdgeIndex === -1) {
			set((state) => ({
				edges: [...state.edges, newEdge],
				hasChanges: true,
			}))
		}
	},

	updateEdge: (id, updates) => {
		set((state) => ({
			edges: state.edges.map((edge) => (edge.id === id ? { ...edge, ...updates } : edge)),
			hasChanges: true,
		}))
	},

	deleteEdge: (id) => {
		set((state) => ({
			edges: state.edges.filter((edge) => edge.id !== id),
			hasChanges: true,
		}))
	},

	setHasChanges: (value) => set({ hasChanges: value }),
	selectNode: (nodeId) => set({ selectedNode: get().nodes.find((n) => n.id === nodeId) || null }),
	editNode: (nodeId) => set({ editingNode: get().nodes.find((n) => n.id === nodeId) || null }),
	resetChanges: () => set({ hasChanges: false }),
	setDiagramDefinition: (definition) => set({ diagramDefinition: definition }),
}))
